import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FactoryIcon from "@mui/icons-material/Factory";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import WarningIcon from "@mui/icons-material/Warning";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BuildIcon from "@mui/icons-material/Build";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

// MUI Components
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import MuiCard from "../../components/mui/MuiCard";
import MuiStatusChip from "../../components/mui/MuiStatusChip";

// API
import {
  getTypes,
  getMaterials,
  getTasks,
  getProducts,
  patchTask,
  patchProduct,
  postTask,
  postInvoice,
} from "../../helpers/api_helper";

/**
 * MuiSection - Modern Production Section Management
 *
 * UX Laws Applied:
 * - Jakob's Law: Familiar production workflow patterns
 * - Hick's Law: Section selector reduces complexity
 * - Law of Proximity: Related task info grouped together
 * - Von Restorff Effect: Action buttons highlighted
 * - Miller's Law: Limited columns, chunked information
 * - Fitts's Law: Large action buttons for production floor use
 */

const MuiSection = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  document.title = `${t("Production Section")} | FlexFlow ERP`;

  // State
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(
    location.state || ""
  );
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [materialsList, setMaterialsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Selected task state
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskMats, setSelectedTaskMats] = useState([]);
  const [taskComments, setTaskComments] = useState("");
  const [operationMode, setOperationMode] = useState("normal"); // "normal" or "override"

  // Drawer state for material replacement
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [materialSearch, setMaterialSearch] = useState("");
  const [replacingMaterialIndex, setReplacingMaterialIndex] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [typesData, materialsData, tasksData] = await Promise.all([
          getTypes(),
          getMaterials(),
          getTasks(),
        ]);
        setSections(Array.isArray(typesData) ? typesData : []);
        setMaterialsList(Array.isArray(materialsData) ? materialsData : []);
        setAllTasks(Array.isArray(tasksData) ? tasksData : []);
      } catch (error) {
        console.error("Error fetching data:", error);
        showSnackbar(t("Failed to load data"), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update section from location state
  useEffect(() => {
    if (location.state) {
      setSelectedSection(location.state);
    }
  }, [location.state]);

  // Filter tasks by section
  useEffect(() => {
    if (selectedSection && allTasks.length > 0) {
      const filtered = allTasks.filter((task) => task.type === selectedSection);
      setTasks(filtered);
      setSelectedTask(null);
      setSelectedTaskMats([]);
    }
  }, [selectedSection, allTasks]);

  // Refresh tasks
  const refreshTasks = async () => {
    try {
      const tasksData = await getTasks();
      setAllTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (error) {
      console.error("Error refreshing tasks:", error);
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Get product stock
  const getProductStock = async (id) => {
    const product = await getProducts(id);
    return product?.[0]?.stock || 0;
  };

  // Update materials with current stock
  const updateMaterialsStock = async (materials) => {
    if (!materials || materials.length === 0) return [];
    const updated = await Promise.all(
      materials.map(async (mat) => {
        const stock = await getProductStock(mat.id);
        return { ...mat, stock };
      })
    );
    return updated;
  };

  // Handle task selection
  const handleTaskSelect = async (task) => {
    setSelectedTask(task);
    setTaskComments(task.comments || "");
    try {
      const materials = JSON.parse(task.materials || "[]");
      const updatedMats = await updateMaterialsStock(materials);
      setSelectedTaskMats(updatedMats);
    } catch (error) {
      console.error("Error parsing materials:", error);
      setSelectedTaskMats([]);
    }
  };

  // Check if all stock received
  const checkAllStockReceived = () => {
    return selectedTaskMats.every((mat) => mat.stockReceived);
  };

  // Calculate required quantity
  const calculateRequired = (mat) => {
    return parseFloat(selectedTask?.quantity || 0) * parseFloat(mat.quantity || 0);
  };

  // Check if stock is sufficient
  const isStockSufficient = (mat) => {
    const required = calculateRequired(mat);
    return parseFloat(mat.stock || 0) >= required;
  };

  // Handle stock received
  const handleStockReceived = async (mat, index) => {
    setSaving(true);
    try {
      const required = calculateRequired(mat);
      const newStock = parseFloat(mat.stock) - required;

      // Update product stock
      await patchProduct({
        resource: [{ id: mat.id, stock: newStock.toFixed(4) }],
      });

      // Update local state
      const newMats = [...selectedTaskMats];
      newMats[index] = { ...mat, stockReceived: true, pendingstock: false };
      setSelectedTaskMats(newMats);

      // Update task
      await patchTask({
        resource: [
          {
            id: selectedTask.id,
            materials: JSON.stringify(newMats),
          },
        ],
      });

      showSnackbar(t("Stock received successfully"));
    } catch (error) {
      console.error("Error receiving stock:", error);
      showSnackbar(t("Failed to receive stock"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle proceed to production
  const handleProceed = async () => {
    setSaving(true);
    try {
      await patchTask({
        resource: [
          {
            id: selectedTask.id,
            materials: JSON.stringify(selectedTaskMats),
            comments: taskComments,
            status: "STOCK-RECEIVED",
          },
        ],
      });

      setSelectedTask({ ...selectedTask, status: "STOCK-RECEIVED" });
      showSnackbar(t("Task ready for production"));
      await refreshTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      showSnackbar(t("Failed to update task"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle finish task
  const handleFinish = async () => {
    setSaving(true);
    try {
      // Update task status
      await patchTask({
        resource: [
          {
            id: selectedTask.id,
            status: "FINISHED",
          },
        ],
      });

      // Update product stock
      const currentStock = await getProductStock(selectedTask.productid);
      await patchProduct({
        resource: [
          {
            id: selectedTask.productid,
            stock: String(parseFloat(currentStock) + parseFloat(selectedTask.quantity)),
          },
        ],
      });

      showSnackbar(t("Task completed successfully"));
      setSelectedTask(null);
      setSelectedTaskMats([]);
      await refreshTasks();
    } catch (error) {
      console.error("Error finishing task:", error);
      showSnackbar(t("Failed to complete task"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle create stock order
  const handleCreateStockOrder = async (mat, index) => {
    setSaving(true);
    try {
      const orderContent = {
        ...mat,
        quantity: calculateRequired(mat),
      };

      await postInvoice({
        resource: [
          {
            providerid: 9999,
            importDate: new Date().toISOString(),
            order_ref: selectedTask.orderref,
            section: selectedTask.type,
            type: "REQUEST-PM",
            content: JSON.stringify([orderContent]),
          },
        ],
      });

      // Update local state
      const newMats = [...selectedTaskMats];
      newMats[index] = { ...mat, stockOrdered: true };
      setSelectedTaskMats(newMats);

      // Update task
      await patchTask({
        resource: [
          {
            id: selectedTask.id,
            materials: JSON.stringify(newMats),
          },
        ],
      });

      showSnackbar(t("Stock order created"));
    } catch (error) {
      console.error("Error creating stock order:", error);
      showSnackbar(t("Failed to create order"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle create sub-task
  const handleCreateSubTask = async (mat) => {
    setSaving(true);
    try {
      const reqs = JSON.parse(mat.requirements || "[]");
      for (const req of reqs) {
        await postTask({
          resource: [
            {
              type: req.typid,
              materials: JSON.stringify(req.materials),
              status: "PENDING",
              orderref: selectedTask.orderref,
              taskref: selectedTask.id,
              productid: mat.id,
              name: mat.name,
              description: "INTERNAL-TASK",
              quantity: calculateRequired(mat).toFixed(4),
              expectedDate: selectedTask.expectedDate,
              customer_id_ref: selectedTask.customer_id_ref,
              seq: selectedTask.seq,
            },
          ],
        });
      }

      showSnackbar(t("Sub-task created"));
      await refreshTasks();
    } catch (error) {
      console.error("Error creating sub-task:", error);
      showSnackbar(t("Failed to create sub-task"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Open material replacement drawer
  const openReplacementDrawer = (index) => {
    setReplacingMaterialIndex(index);
    setDrawerOpen(true);
  };

  // Handle material replacement
  const handleReplaceMaterial = async (newMaterial) => {
    if (replacingMaterialIndex === null) return;

    const newMats = [...selectedTaskMats];
    newMats[replacingMaterialIndex] = {
      ...newMaterial,
      quantity: selectedTaskMats[replacingMaterialIndex].quantity,
    };
    setSelectedTaskMats(newMats);
    setDrawerOpen(false);
    setReplacingMaterialIndex(null);

    showSnackbar(t("Material replaced"));
  };

  // Filtered materials for drawer
  const filteredMaterials = useMemo(() => {
    if (!materialSearch) {
      return materialsList.filter(
        (m) => m.stock > 0 && m.usageType !== "COMPLETE-PRODUCT"
      );
    }
    const searchRegex = new RegExp(materialSearch, "i");
    return materialsList.filter(
      (m) => searchRegex.test(m.name) || searchRegex.test(m.description)
    );
  }, [materialsList, materialSearch]);

  // Check for pending sub-tasks
  const checkPendingSubTasks = (mat) => {
    return allTasks.filter(
      (task) =>
        parseFloat(task.taskref) === parseFloat(selectedTask?.id) &&
        task.orderref === selectedTask?.orderref &&
        task.productid === mat.id &&
        task.status !== "FINISHED"
    );
  };

  // Get status chip color
  const getStatusColor = (status) => {
    const map = {
      PENDING: "pending",
      "STOCK-RECEIVED": "processing",
      FINISHED: "completed",
    };
    return map[status] || "default";
  };

  // DataGrid columns
  const columns = [
    {
      field: "name",
      headerName: t("Product"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "quantity",
      headerName: t("Quantity"),
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: "expectedDate",
      headerName: t("Expected Date"),
      width: 130,
    },
    {
      field: "status",
      headerName: t("Status"),
      width: 140,
      renderCell: (params) => (
        <MuiStatusChip
          status={getStatusColor(params.value)}
          label={params.value}
          size="small"
        />
      ),
    },
    {
      field: "order.id",
      headerName: t("Order ID"),
      width: 100,
      valueGetter: (value, row) => row.order?.id || "-",
    },
    {
      field: "order.name",
      headerName: t("Customer"),
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.order?.name || "-",
    },
    {
      field: "order.expected_delivery_date",
      headerName: t("Delivery Date"),
      width: 130,
      valueGetter: (value, row) => row.order?.expected_delivery_date || "-",
    },
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: t("Production"), path: "/tasks" },
    { label: t("Section") },
  ];

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false}>
        {/* Page Header */}
        <MuiPageHeader
          title={t("Production Section")}
          subtitle={
            selectedSection
              ? `${t("Managing")} ${selectedSection} ${t("section")}`
              : t("Select a section to begin")
          }
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              variant="outlined"
              startIcon={<FactoryIcon />}
              onClick={() => navigate("/tasks")}
            >
              {t("Back to Tasks")}
            </Button>
          }
        />

        {/* Section Selector */}
        <MuiCard sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label={t("Select Section")}
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FactoryIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              >
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.name}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={section.floor || "-"}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ minWidth: 40 }}
                      />
                      {section.name}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Chip
                  icon={<InventoryIcon />}
                  label={`${tasks.length} ${t("tasks")}`}
                  color="primary"
                />
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${tasks.filter((t) => t.status === "FINISHED").length} ${t(
                    "completed"
                  )}`}
                  color="success"
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`${tasks.filter((t) => t.status === "PENDING").length} ${t(
                    "pending"
                  )}`}
                  color="warning"
                />
              </Box>
            </Grid>
          </Grid>
        </MuiCard>

        {/* Tasks DataGrid */}
        <MuiCard
          title={`${selectedSection || t("Section")} - ${t("Tasks")}`}
          noPadding
          sx={{ mb: 3 }}
        >
          <DataGrid
            rows={tasks}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            onRowClick={(params) => handleTaskSelect(params.row)}
            autoHeight
            sx={{
              border: 0,
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
              "& .MuiDataGrid-row.Mui-selected": { backgroundColor: "primary.50" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "grey.50",
                borderBottom: 2,
                borderColor: "divider",
              },
            }}
            localeText={{
              noRowsLabel: t("No tasks in this section"),
            }}
          />
        </MuiCard>

        {/* Selected Task Detail */}
        {selectedTask && (
          <MuiCard>
            {/* Task Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
                  <BuildIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedTask.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("Order")} #{selectedTask.order?.id} • {t("Quantity")}:{" "}
                    {selectedTask.quantity}
                  </Typography>
                </Box>
              </Box>
              <MuiStatusChip
                status={getStatusColor(selectedTask.status)}
                label={selectedTask.status}
                size="medium"
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Finished Task View */}
            {selectedTask.status === "FINISHED" && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                <Typography variant="subtitle1">
                  {t("Task completed on")} {selectedTask.updated}
                </Typography>
                <Typography variant="body2">
                  {selectedTask.product?.name} • {t("Quantity")}: {selectedTask.quantity}
                </Typography>
              </Alert>
            )}

            {/* Active Task View */}
            {selectedTask.status !== "FINISHED" && (
              <>
                {/* Operation Mode */}
                {selectedTask.status === "PENDING" && (
                  <Box sx={{ mb: 3 }}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">{t("Operation Mode")}</FormLabel>
                      <RadioGroup
                        row
                        value={operationMode}
                        onChange={(e) => setOperationMode(e.target.value)}
                      >
                        <FormControlLabel
                          value="normal"
                          control={<Radio />}
                          label={t("Normal Mode")}
                        />
                        <FormControlLabel
                          value="override"
                          control={<Radio />}
                          label={t("Override Mode")}
                        />
                      </RadioGroup>
                    </FormControl>

                    {checkAllStockReceived() && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleProceed}
                        disabled={saving}
                        sx={{ ml: 2 }}
                      >
                        {t("Proceed to Production")}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Materials Section */}
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {selectedTask.status === "PENDING"
                    ? t("Materials Required - Get from Stock")
                    : t("Materials Ready - Execute Task")}
                </Typography>

                {selectedTaskMats.map((mat, index) => (
                  <MuiCard
                    key={index}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      bgcolor: mat.stockReceived
                        ? "success.50"
                        : isStockSufficient(mat)
                        ? "info.50"
                        : "warning.50",
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      {/* Material Info */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CategoryIcon color="action" />
                          <Typography variant="subtitle2">{mat.name}</Typography>
                        </Box>
                      </Grid>

                      {/* Stock Info */}
                      <Grid item xs={12} md={4}>
                        {!mat.stockReceived ? (
                          <Box>
                            <Typography
                              variant="body2"
                              color={isStockSufficient(mat) ? "success.main" : "error.main"}
                            >
                              {t("Required")}: {selectedTask.quantity} × {mat.quantity}{" "}
                              {mat.unit} = {calculateRequired(mat).toFixed(2)} {mat.unit}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("Stock")}: {mat.stock} {mat.unit}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={t("Stock on Section")}
                            color="success"
                            size="small"
                          />
                        )}
                      </Grid>

                      {/* Actions */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {selectedTask.status === "PENDING" && !mat.stockReceived && (
                            <>
                              {isStockSufficient(mat) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleStockReceived(mat, index)}
                                  disabled={saving}
                                >
                                  {t("Stock Received")}
                                </Button>
                              )}

                              {!isStockSufficient(mat) && (
                                <>
                                  {mat.usageType === "PURCHASED-MATERIAL" &&
                                    !mat.stockOrdered && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<AddShoppingCartIcon />}
                                        onClick={() =>
                                          handleCreateStockOrder(mat, index)
                                        }
                                        disabled={saving}
                                      >
                                        {t("Order Stock")}
                                      </Button>
                                    )}

                                  {mat.usageType === "MATERIAL" &&
                                    checkPendingSubTasks(mat).length === 0 && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<BuildIcon />}
                                        onClick={() => handleCreateSubTask(mat)}
                                        disabled={saving}
                                      >
                                        {t("Create Task")}
                                      </Button>
                                    )}

                                  {checkPendingSubTasks(mat).length > 0 && (
                                    <Chip
                                      icon={<WarningIcon />}
                                      label={t("Pending Tasks")}
                                      color="warning"
                                      size="small"
                                    />
                                  )}

                                  {mat.stockOrdered && (
                                    <Chip
                                      icon={<LocalShippingIcon />}
                                      label={t("Stock Ordered")}
                                      color="info"
                                      size="small"
                                    />
                                  )}
                                </>
                              )}

                              {operationMode === "override" && mat.overidable && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SwapHorizIcon />}
                                  onClick={() => openReplacementDrawer(index)}
                                  disabled={saving}
                                >
                                  {t("Replace")}
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </MuiCard>
                ))}

                {/* Comments & Finish */}
                {(selectedTask.status === "PENDING" ||
                  selectedTask.status === "STOCK-RECEIVED") && (
                  <Box sx={{ mt: 3 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t("Comments")}
                      value={taskComments}
                      onChange={(e) => setTaskComments(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        color="error"
                        size="large"
                        startIcon={<DoneAllIcon />}
                        onClick={handleFinish}
                        disabled={saving}
                        sx={{ minWidth: 200, minHeight: 60 }}
                      >
                        {saving ? <CircularProgress size={24} /> : t("Finish Task")}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </MuiCard>
        )}
      </Container>

      {/* Material Replacement Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">{t("Select Replacement Material")}</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder={t("Search materials...")}
            value={materialSearch}
            onChange={(e) => setMaterialSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <List sx={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}>
            {filteredMaterials.map((mat) => (
              <ListItem
                key={mat.id}
                button
                onClick={() => handleReplaceMaterial(mat)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemIcon>
                  {mat.usageType === "MATERIAL" && <BuildIcon color="primary" />}
                  {mat.usageType === "PURCHASED-MATERIAL" && (
                    <ShoppingCartIcon color="secondary" />
                  )}
                  {mat.usageType === "COMPLETE-PRODUCT" && (
                    <CategoryIcon color="success" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={mat.name}
                  secondary={`${t("Stock")}: ${mat.stock} ${mat.unit}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuiSection;
